import { Suspense, useCallback, useState } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, useSnackbar } from '@lyearn/ui';
import Loader from '@/components/Loader';
import { Dialog } from '@/components/Modals';
import useNavigation from '@/hooks/useNavigation';
import useResponse from '@/hooks/useResponse';
import routes from '@/pages/corporate/routes';

import { useDuplicateFileMutation } from '../../../graphql/mutations/generated/duplicateFile';
import { DialogProps } from '../types';
import Editor from './components/Editor';
import { DuplicateProjectEditorFormValues, getInitialValues } from './components/Editor/config';
import { adaptPropertiesForMutation } from './utils';

export const dialogSize = css`
  max-width: 60.3rem;
  height: 80rem;
`;

function DuplicateProject({ onClose, duplicateFromFileId, title }: DialogProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formikSubmit, setFormikSubmit] = useState<any>();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();
  const [, duplicateFile] = useDuplicateFileMutation();
  const navigation = useNavigation();

  const onSubmit = useCallback(
    async (formValues: DuplicateProjectEditorFormValues) => {
      setIsSubmitting(true);
      const { duplicateFromId, title, properties } = formValues;
      const { propertiesToDuplicate, actionItemPropertiesToDuplicate } =
        adaptPropertiesForMutation(properties);
      const response = await duplicateFile({
        data: {
          duplicateFromId,
          name: title,
          propertiesToDuplicate,
          actionItemPropertiesToDuplicate,
        },
      });
      setIsSubmitting(false);
      const newFileId = response.data?.duplicateFile?.file?.id;
      handleResponse({
        condition: Boolean(newFileId),
        onSuccess: () => {
          showSnackbar({
            message: t('Task:DuplicateProject.SuccessMessage'),
          });
          newFileId && navigation.push(routes.ProjectsDashboard(newFileId));
          onClose();
        },
        onError: () => {
          showErrorSnackbar({
            message: t('Task:DuplicateProject.ErrorMessage'),
          });
        },
      });
    },
    [duplicateFile, handleResponse, onClose, showSnackbar, showErrorSnackbar, navigation, t],
  );

  return (
    <Dialog
      open
      action={{
        disabled: isSubmitting,
        isSubmitting: isSubmitting,
        text: t('Common:Actions.Duplicate'),
        onSubmit: formikSubmit,
        buttonSize: 'large',
      }}
      className="h-full"
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      closeButton={{
        size: 'large',
        className: 'end-12 top-20',
      }}
      header={{
        title: t('Task:DuplicateProject.Title'),
        description: t('Task:DuplicateProject.Description'),
        variants: {
          description: 'body-short-01',
        },
        className: 'pt-28 pb-24',
      }}
      onClose={onClose}>
      <Box className="px-24 pt-8 pb-24">
        <Suspense fallback={<Loader className="mx-auto my-auto" />}>
          <Editor
            initialValues={getInitialValues({ duplicateFromId: duplicateFromFileId, title, t })}
            setFormikSubmit={setFormikSubmit}
            onSubmit={onSubmit}
          />
        </Suspense>
      </Box>
    </Dialog>
  );
}

export default DuplicateProject;
